import { isPortalHost, restartPortalLogin } from '../auth/portal.js';
import { ApiClientError, parseApiErrorResponse } from './errors.js';

// VITE_API_URL is for local dev only (e.g. http://localhost:8080 when not using Vite proxy).
// In production the UI and API are co-located (SERVE_UI=true), so all requests are same-origin.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

// When API_BASE is set to a different origin, use 'include' so cookies are sent cross-origin.
// In production (same-origin), 'same-origin' avoids sending cookies to unrelated origins.
const CREDENTIALS: RequestCredentials = API_BASE ? 'include' : 'same-origin';

/**
 * Creates an API fetch function that sends the HttpOnly session cookie automatically.
 * Auth is handled server-side via WorkOS sealed sessions — no Bearer token required.
 */
export function createApiFetch() {
	return async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
		const res = await fetch(`${API_BASE}${path}`, {
			...options,
			credentials: CREDENTIALS,
			headers: {
				'Content-Type': 'application/json',
				...options.headers,
			},
		});

		if (res.status === 401) {
			if (isPortalHost()) {
				restartPortalLogin();
			} else {
				window.location.href = `/api/auth/login?return_to=${encodeURIComponent(window.location.pathname)}`;
			}
			throw new ApiClientError(401, { error: 'session_expired', message: 'Session expired' }, '');
		}

		if (!res.ok) {
			throw await parseApiErrorResponse(res);
		}

		// 204 No Content — return undefined cast to T
		if (res.status === 204) {
			return undefined as unknown as T;
		}

		return res.json() as Promise<T>;
	};
}
